<template>
    <div>
		<v-dialog
			v-model="dialog"
			persistent
			fullscreen
			transition="slide-x-reverse-transition"
		>
            <v-card flat>
                <!-- title -->
                <v-toolbar dark flat color="primary" class="text-uppercase">
                    <v-spacer/>
                    {{title}}
                    <v-spacer/>
                    <v-toolbar-items text>
                        <v-btn small text @click="dialog=false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                
                <v-form @submit.prevent="submit" ref="form">
                    <!-- card content -->
                    <v-card-text class="py-0">
                        <v-container>
                            <v-row dense>
								<v-col v-if="id === null && customersTypes.length" cols="12" align-self="center">
									<v-chip-group
										v-model="dirType"
										show-arrows
										center-active
										active-class="success"
										class="d-flex justify-center pt-0"
										mandatory
										@change="reset"
									>
										<v-chip 
											v-for="(type, index) in customersTypes"
											:key="index"
											:value="type.id"
											class="rounded-xl"
										>
											{{ type.name }}
										</v-chip>
									</v-chip-group>
								</v-col>
                                <!-- date -->
                                <v-col cols="12" class="d-flex">
									<div class="d-flex flex-column align-start justify-start me-1">
										<v-icon size="24" class="mx-1" :color="isHoliday === 'error' ? '#F4511E' : ''">mdi-calendar</v-icon>
										<span v-if="isHoliday === 'error'" style="color: #F4511E" class=" fs-sm">Holiday </span>
									</div>
									<v-menu
										v-model="visitDateMenu"
										:close-on-content-click="false"
										offset-y
									>
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												v-model="visitDate"
												:label="$T('inputs.date')"
												hide-details="auto"
												dense
												:disabled="Boolean(id)"
												readonly
												:rules="rules.date"
												v-bind="attrs"
												v-on="on"
											></v-text-field>
										</template>
										<v-date-picker
											v-model="visitDate"
											:disabled="Boolean(id)"
											:min="moment().format('YYYY-MM-DD')"
											:max="moment().format('YYYY-MM-DD')"
											@input="visitDateMenu = false"
										></v-date-picker>
									</v-menu>
                                </v-col>
								<!-- region -->
                                <v-col md="6" cols="12">
									<v-autocomplete
										v-model="regionId"
										:items="regions"
										item-text="name"
										item-value="id"
										:label="$T('inputs.region')"
										hide-details
										@change="fetchEntities"
										clearable
									></v-autocomplete>
                                </v-col>

                                <!-- entity -->
                                <v-col md="6" cols="12" class="d-flex align-end">
									<v-autocomplete
										v-model="entityId"
										:items="allEntities"
										item-text="name"
										item-value="id"
										hide-details
										:search-input.sync="entitySearchInput"
										:label="$T('inputs.customer-name')"
										:loading="EntitiesLoading"
										:disabled="EntitiesLoading"
										:rules="rules.required"
										@change="updateItemsFromSpecialtyMaterials"
										v-on="{
											focus: () =>
												allEntities.length && 
												!allEntities[allEntities.length - 1].id ? allEntities.splice(-1, 1) : null,
											blur: () =>
												allEntities.push({ id: null, name: entitySearchInput }),
										}"
										clearable
									>
										<template
											v-if="showEntityAddressInMenus"
											v-slot:item="{ attrs, on, item }"
										>
											<v-list-item
												v-show="item.id"
												v-on="item.id ? on : {}"
												v-bind="item.id ? attrs : {}"
											>
												<v-list-item-content class="py-0">
													<v-list-item-title class="mb-0">
														{{item.name}}
													</v-list-item-title>
													<v-list-item-subtitle
														v-if="
															Array.isArray(item.entityAddresses) &&
															item.entityAddresses?.length
														"
													>
														<v-icon size="16">mdi-map-marker</v-icon>
														<span class="text-subtitle-2">
															{{
																item.entityAddresses
																	.map(address => address.address).join(', ')
															}}
														</span>
													</v-list-item-subtitle>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-autocomplete>

									<v-alert
										v-if="useVisitLocationCheck && locationValid === false"
										type="warning"
										border="left"
										dense
										class="mt-3 mb-0"
									>
										{{ locationError || 'Location check failed. Please make sure you are at the customer location.' }}
									</v-alert>
									
									<v-btn
										v-if="addingCustomerDirectly"
										icon
										:disabled="EntitiesLoading || regionId === null"
										class="ms-2"
										@click="
											$emit('add-customer', {
												name: entitySearchInput,
												regionId: regionId
											})
										"
									>
										<v-icon>mdi-account-plus</v-icon>
									</v-btn>

									<v-btn
										icon
										class="ms-2"
										:disabled="EntitiesLoading"
										@click="openScanDialog"
									>
										<v-icon>mdi-qrcode-scan</v-icon>
									</v-btn>
                                </v-col>

								<v-col v-if="availableRepTabs.length > 1" cols="12" class="pb-0">
									<v-tabs v-model="dialogSectionTab" grow background-color="blue-grey lighten-5" color="primary">
										<v-tab
											v-for="section in availableRepTabs"
											:key="section"
										>
											<v-icon small left>{{ section === 'Scientific' ? 'mdi-flask' : 'mdi-trending-up' }}</v-icon>
											<span v-if="salesSystem"> 
												{{ section === 'Financial' ? $T('Visits.tab-commercial') : $T('Visits.tab-marketing') }}
											</span>
											<span v-else> 
												{{ section === 'Financial' ?  $T('Visits.tab-marketing') : $T('Visits.tab-scientific')}}
											</span>
										</v-tab>
									</v-tabs>
								</v-col>

								<!-- visit notes -->
								<v-col v-if="showVisitFieldForSection('VisitNotes', configIsEmpty(visitsConfig) === false ? visitsConfig?.VisitNotes : true, 'shared')" md="6" cols="12">
									<v-text-field
										v-model="visitNotes"
										:label="$T('inputs.visit-notes')"
										prepend-icon="mdi-note-multiple-outline"
										hide-details
									></v-text-field>
								</v-col>

								<v-col v-if="showVisitFieldForSection(['Objection', 'Objections'], configIsEmpty(visitsConfig) === false ? visitsConfig?.Objection : true, 'shared')" md="6" cols="12">
									<v-text-field
										v-model="objection"
										:label="$T('inputs.objection')"
										prepend-icon="mdi-alert-circle-outline"
										hide-details
									></v-text-field>
								</v-col>

								<!-- scientific: coreMessage -->
								<v-col v-if="showVisitFieldForSection('CoreMessage', true, 'scientific')" md="6" cols="12">
									<v-text-field
										v-model="coreMessage"
										:label="$T('inputs.core-message')"
										prepend-icon="mdi-note-outline"
										hide-details
									></v-text-field>
								</v-col>

								<!-- scientific: durationInMinutes -->
								<v-col v-if="showVisitFieldForSection('DurationInMinute', false, 'scientific')" md="6" cols="12">
									<v-text-field
										v-model.number="durationInMinutes"
										:label="$T('inputs.duration-minutes')"
										type="number"
										hide-spin-buttons
										:suffix="$T('inputs.minutes')"
										min="0"
										prepend-icon="mdi-clock-outline"
										hide-details
									></v-text-field>
								</v-col>

								<!-- scientific: purpose -->
								<v-col v-if="showVisitFieldForSection('Purpose', configIsEmpty(visitsConfig) === false ? visitsConfig?.Purpose : true, 'scientific')" md="6" cols="12">
									<v-text-field
										v-model="nextCall"
										:label="$T('inputs.next-call')"
										prepend-icon="mdi-check"
										hide-details
									></v-text-field>
								</v-col>

								<!-- scientific: nextVisitNotes -->
								<v-col v-if="showVisitFieldForSection('NextVisitNotes', true, 'scientific')" md="6" cols="12">
									<v-text-field
										v-model="centerNotes"
										:label="$T('inputs.notes')"
										prepend-icon="mdi-note-multiple-outline"
										hide-details
									></v-text-field>
								</v-col>

								<!-- marketing: visitPriceType -->
								<v-col v-if="showVisitFieldForSection('VisitPriceType', false, 'financial')" md="6" cols="12">
									{{$T('inputs.pricing-types')}}
									<v-radio-group
										v-model="priceTypeId"
										mandatory
										row
										hide-details
										class="pt-0 my-2"
										@change="updateSamplesPrices"
									>
										<v-radio
											v-for="priceType in priceTypes"
											:key="priceType.id"
											:label="priceType.name"
											:value="priceType.id"
											class="mx-auto"
											color="success"
										/>
									</v-radio-group>
								</v-col>

								<!-- marketing: employeeWithDeal -->
								<v-col v-if="showVisitFieldForSection('EmployeeDealtWith', false, 'financial')">
									<div class="d-flex flex-grow-1 justify-space-between align-center">
										<div class="d-flex align-center">
											<v-icon class="me-2">mdi-account</v-icon>
											<span class="text-body-1">{{$T('inputs.persons')}}</span>
										</div>

										<v-btn
											text
											@click="personsNames.push({
												id: personsNames.length,
												name: null,
											})"
										>
											<v-icon color="grey darken-3">mdi-plus-circle-outline</v-icon>
										</v-btn>
									</div>
								
									<v-card v-if="personsNames.length > 0" class="my-2">
										<v-card-text>
											<v-row
												v-for="(person, index) in personsNames"
												:key="person.id"
												dense
												class="align-center"
											>
												<!-- sample -->
												<v-col cols="10">
													<v-text-field
														v-model="person.name"
														:placeholder="$T('inputs.name')"
														dense
														hide-details="auto"
														:rules="!id ? rules.required : []"
													></v-text-field>
												</v-col>

												<v-col cols="2" class="pa-0">
													<v-btn
														text
														small
														@click="
															personsNames.forEach((c, i) => {
																if (i > index) personsNames[i].id--;
															})
															personsNames.splice(index, 1);
														"
													>
														<v-icon color="grey darken-3">mdi-minus-circle-outline</v-icon>
													</v-btn>
												</v-col>
											</v-row>
										</v-card-text>
									</v-card>
								</v-col>

								<!-- marketing: materialAmount + freeSample -->
								<v-col v-if="showVisitFieldForSection('MaterialAmount', true, 'financial')" cols="12">
									<div class="d-flex flex-grow-1 justify-space-between align-center">
										<div class="d-flex align-center">
											<v-icon class="me-2">mdi-pill</v-icon>
											<span class="text-body-1">{{$T('inputs.samples')}}</span>
										</div>

										<v-spacer/>

										<!-- restore planed materials specialty -->
										<v-btn
											v-if="
												useMaterialsSpecialtyVisits &&
												isFilledPlanedSamplesFilled &&
												isSamplesPlanedChanged
											"
											icon
											color="grey darken-3"
											@click="updateItemsFromSpecialtyMaterials"
										>
											<v-icon>mdi-restore</v-icon>
										</v-btn>

										<!-- add sample -->
										<v-btn text @click="addSample">
											<v-icon color="grey darken-3">mdi-plus-circle-outline</v-icon>
										</v-btn>
									</div>
								
									<!-- sample card -->
									<v-card v-if="samples.length > 0" class="mb-2">
										<v-card-text class="px-4 py-2">
											<v-row
												v-for="sample in samples"
												:key="sample.id"
												dense
												class="align-center"
											>
												<!-- sample -->
												<v-col cols="7">
													<v-autocomplete
														v-model="sample.data.materialId"
														:items="materials"
														:item-text="getFullMaterialName"
														item-value="id"
														:placeholder="$T('inputs.sample')"
														dense
														hide-details
														:rules="rules.required"
														@change="id => {
															sample.data.price =
																!sample.data.isFree
																	? getMaterialPrice(sample.data.materialId)
																	: 0;
															isSamplesPlanedChanged = true;
														}"
													></v-autocomplete>
												</v-col>

												<!-- amount -->
												<!-- <v-col :cols="samples.length > 1 ? 3 : 5"> -->
												<v-col :cols="3">
													<v-text-field
														v-model.number="sample.data.amount"
														:placeholder="$T('inputs.amount')"
														type="number"
														min="0"
														dense
														hide-spin-buttons
														hide-details
														:rules="[...rules.number, val => val > 0]"
														@change="isSamplesPlanedChanged = true;"
													></v-text-field>
												</v-col>
												<!-- use price because price not found in configuration yet! -->
												<template v-if="usePrices">
													<!-- price -->
													<v-col :cols="3" class="pt-2">
														<v-text-field
															v-model.number="sample.data.price"
															:label="$T('inputs.price')"
															type="number"
															min="0"
															dense
															disabled
															hide-spin-buttons
															hide-details
														></v-text-field>
													</v-col>
													
													<!-- discount -->
													<v-col :cols="3" class="pt-2">
														<v-text-field
															v-model.number="sample.data.discount"
															:label="$T('inputs.discount')"
															type="number"
															min="0"
															dense
															hide-spin-buttons
															hide-details
															suffix="%"
															:rules="[
																val => checkMaterialDiscountLimit(sample.data.materialId, val)
															]"
														></v-text-field>
													</v-col>
													
													<!-- isFree -->
													<!-- visitsConfig?.FreeSample -->
													<v-col v-if="showVisitFieldForSection('FreeSample', false, 'financial')" :cols="3" class="pt-2">
														<v-checkbox
															v-model.number="sample.data.isFree"
															:label="$T('inputs.free')"
															dense
															hide-details
															@change="val => {
																sample.data.price =
																	!val
																		? getMaterialPrice(sample.data.materialId)
																		: 0;
															}"
														></v-checkbox>
													</v-col>
												</template>

												<!-- remove button -->
												<v-col cols="2" class="pa-0">
													<v-btn text small @click="removeSample(sample.id)">
														<v-icon color="grey darken-3">mdi-minus-circle-outline</v-icon>
													</v-btn>
												</v-col>

												<v-col cols="12" class="py-2">
													<v-divider/>
												</v-col>
											</v-row>

											<p v-if="usePrices" class="mb-0">
												<span class="text-body-2 me-2">{{$T('Visits.total')}}:</span>
												<span class="font-weight-bold">{{ $options.filters.formatNumber(total, 3) }}</span>
											</p>
										</v-card-text>
									</v-card>
								</v-col>

								<!-- marketing: unavailableMaterial -->
								<v-col 
									md="6" 
									cols="12" 
									v-if="
										showVisitFieldForSection(['UnavailableMaterial', 'UnavailableMaterials'], true, 'Both')
									"
								>
									<v-autocomplete
										v-model="unavailableMaterialIds"
										:items="allMaterials"
										:item-text="getFullMaterialName"
										item-value="id"
										:label="$T('inputs.unavailable-products')"
										dense
										multiple
										hide-details="auto"
										prepend-icon="mdi-close"
										class="mt-1"
									>
										<template v-slot:selection="{ item, index }">
											<span v-if="index === 0" class="me-1">{{ getFullMaterialName(item) }}</span>
											<span
												v-if="index === 1"
												class="grey--text text-caption"
											>
												(+{{ unavailableMaterialIds.length - 1 }} others)
											</span>
										</template>
									</v-autocomplete>
								</v-col>

								<!-- marketing: competetive samples -->
								<v-col v-if="showVisitFieldForSection('CompetetiveSample', false, 'financial')" md="6" cols="12">
									<div class="d-flex flex-grow-1 justify-space-between align-center">
										<div class="d-flex align-center">
											<v-icon class="me-2">mdi-trending-up</v-icon>
											<span class="text-body-1">{{$T('inputs.competitors')}}</span>
										</div>

										<v-btn text @click="addCompetitor">
											<v-icon color="grey darken-3">mdi-plus-circle-outline</v-icon>
										</v-btn>
									</div>

									<!-- Competitor card -->
									<v-card v-if="competitors.length > 0" class="my-2">
										<v-card-text>
											<v-row
												v-for="competitor in competitors"
												:key="competitor.id"
												dense
												class="align-center"
											>
												<!-- competitor -->
												<v-col cols="10">
													<v-text-field
														v-model="competitor.value"
														:placeholder="$T('inputs.competitor')"
														dense
														hide-details
														required
													></v-text-field>
												</v-col>

												<!-- remove button -->
												<v-col cols="2" class="pa-0">
													<v-btn
													text
													small
													@click="removeCompetitor(competitor.id)"
													>
														<v-icon color="grey darken-3">mdi-minus-circle-outline</v-icon>
													</v-btn>
												</v-col>
											</v-row>
										</v-card-text>
									</v-card>
								</v-col>

								<!-- marketing: other materials -->
								<v-col v-if="showVisitFieldForSection('OtherMaterial', configIsEmpty(visitsConfig) === false ? visitsConfig?.OtherMaterial : true, 'financial')" md="6" cols="12">
									<v-text-field
										v-model="otherSamples"
										:label="$T('inputs.other-materials')"
										prepend-icon="mdi-plus-minus-box"
										hide-details
									></v-text-field>
								</v-col>

								<!-- marketing: paymentMethods -->
								<v-col v-if="showVisitFieldForSection('PaymentType', false, 'financial')" md="6" cols="12">
									{{$T('inputs.payment-type')}}
									<v-radio-group
										v-model="paymentMethodId"
										mandatory
										row
										hide-details
										class="pt-0 mt-2"
									>
										<v-radio
											v-for="item in paymentMethods"
											:key="item.id"
											:label="item.name"
											:value="item.id"
											class="mx-auto"
											color="success"
										/>
									</v-radio-group>
								</v-col>

								<!-- marketing: amount received -->
								<v-col
									v-if="paymentMethodId === paymentMethod.debit && showVisitFieldForSection('AmountReceived', false, 'financial')"
									md="6"
									cols="12"
									class="d-flex align-center"
								>
									<span class="text-body-2 me-4">{{$T('inputs.paid-amount')}}</span>
									<v-text-field
										v-model.number="paidAmount"
										type="number"
										hide-details
										hide-spin-buttons
										dense
										:rules="[val => val <= +total && val > 0]"
									/>
								</v-col>

								<!-- local order request section -->
								<!-- test
								{{ useVisitOrderRequest }} -->
								<v-col v-if="useVisitOrderRequest" cols="12">
									<v-expansion-panels flat class="order-expander">
										<v-expansion-panel class="order-expander__panel">
											<v-expansion-panel-header>
												<div class="d-flex align-center justify-space-between w-100 pe-4">
													<div class="d-flex align-center">
														<v-avatar size="32" color="teal lighten-5" class="me-2">
															<v-icon color="teal darken-2" size="18">mdi-cart-variant</v-icon>
														</v-avatar>
														<div>
															<div class="text-subtitle-1 font-weight-bold">{{ $T('Visits.create-order') }}</div>
															<div class="text-caption grey--text text--darken-1">{{ $T('Visits.order-items') }}</div>
														</div>
													</div>
													<v-chip small color="teal lighten-4" text-color="teal darken-3">
														{{ orderItems.length }}
													</v-chip>
												</div>
											</v-expansion-panel-header>
											<v-expansion-panel-content class="order-expander__content">
												<div class="d-flex flex-grow-1 justify-space-between align-center mb-2">
													<span class="text-subtitle-2 font-weight-medium">{{ $T('Visits.order-items') }}</span>
													<v-btn outlined small color="teal darken-1" @click="addOrderItem">
														<v-icon left size="16">mdi-plus</v-icon>
														{{ $T('add') }}
													</v-btn>
												</div>

												<v-card v-if="orderItems.length > 0" outlined class="order-expander__items-card">
													<v-card-text class="px-4 py-2">
														<v-row
															v-for="item in orderItems"
															:key="item.id"
															dense
															class="align-center"
														>
															<v-col cols="7">
																<v-autocomplete
																	v-model="item.data.materialId"
																	:items="allMaterials"
																	:item-text="getFullMaterialName"
																	item-value="id"
																	:placeholder="$T('inputs.sample')"
																	outlined
																	dense
																	hide-details="auto"
																	:rules="rules.orderItemMaterial(item.data.amount)"
																></v-autocomplete>
															</v-col>

															<v-col cols="3">
																<v-text-field
																	v-model.number="item.data.amount"
																	:placeholder="$T('inputs.amount')"
																	type="number"
																	min="1"
																	outlined
																	dense
																	hide-spin-buttons
																	hide-details="auto"
																	:rules="rules.orderItemAmount(item.data.materialId)"
																></v-text-field>
															</v-col>

															<v-col cols="2" class="pa-0">
																<v-btn icon small color="error" @click="removeOrderItem(item.id)">
																	<v-icon size="18">mdi-close-circle-outline</v-icon>
																</v-btn>
															</v-col>

															<v-col cols="12" class="py-2">
																<v-divider/>
															</v-col>
														</v-row>
													</v-card-text>
												</v-card>

												<v-sheet
													v-else
													outlined
													rounded
													class="d-flex align-center justify-center py-6 mb-3 order-expander__empty"
												>
													<div class="text-center">
														<v-icon color="grey lighten-1" size="26">mdi-cart-outline</v-icon>
														<div class="text-caption grey--text text--darken-1 mt-1">{{ $T('Visits.order-items') }}: 0</div>
													</div>
												</v-sheet>

												<v-menu
													v-model="orderDeliveryDateMenu"
													:close-on-content-click="false"
													offset-y
												>
													<template v-slot:activator="{ on, attrs }">
														<v-text-field
															v-model="orderDeliveryDate"
															:label="$T('inputs.delivery-date')"
															readonly
															outlined
															dense
															hide-details="auto"
															prepend-icon="mdi-calendar-clock"
															class="mt-3 order-expander__date-input"
															v-bind="attrs"
															v-on="on"
															:rules="orderItems.length > 0 ? rules.required : []"
															clearable
														></v-text-field>
													</template>
													<v-date-picker
														v-model="orderDeliveryDate"
														:min="visitDate || moment().format('YYYY-MM-DD')"
														@input="orderDeliveryDateMenu = false"
													></v-date-picker>
												</v-menu>
											</v-expansion-panel-content>
										</v-expansion-panel>
									</v-expansion-panels>
								</v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                    <v-alert
						v-if="hasDeliveryDateWithoutItems"
						type="warning"
						dense
						text
						class="mb-0 text-caption"
						>
							{{ $T('Visits.order-delivery-date-without-items') }}
						</v-alert>
                        <v-spacer></v-spacer>
						<v-btn
							color="success"
							text
							type="submit"
							:loading="loading"
							:disabled="loading || (useVisitLocationCheck && (locationChecking || locationValid === false)) || hasDeliveryDateWithoutItems"
						>
                            {{$T('save')}}
                        </v-btn>
                        <v-btn color="grey darken-1" text @click="dialog = false">
                            {{$T('close')}}
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>

		<v-dialog
			v-model="scanDialog"
			max-width="560"
		>
			<v-card>
				<v-toolbar flat color="primary" dark>
					<v-spacer/>
					Scan QR
					<v-spacer/>
				</v-toolbar>

				<v-card-text class="pt-4">
					<qrcode-stream
						v-if="scanDialog"
						@decode="onQrDecode"
						@init="onQrInit"
					/>
					<p v-if="scanError" class="error--text mt-3 mb-0">{{ scanError }}</p>
				</v-card-text>

				<v-card-actions>
					<v-spacer/>
					<v-btn text color="grey darken-1" @click="scanDialog = false">
						{{$T('close')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

    </div>
</template>

<script>
import rules from '@/helpers/validation rules'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { QrcodeStream } from 'vue-qrcode-reader'
import { addingCustomerDirectly, showEntityAddressInMenus, useMaterialsSpecialtyVisits, usePrices, deletionExternal, enableVacations, useVisitOrderRequest, useVisitLocationCheck } from '@/configs/general.configs'
import { paymentMethod, permissionsNamesEnum, rolePermission } from '@/helpers/enums'
import { getDateColor } from '@/helpers/functions'
export default {
	components: {
		QrcodeStream,
	},

	data() {
		return {
			// configs
			paymentMethod,
			usePrices,
			useVisitOrderRequest,
			useVisitLocationCheck,
			showEntityAddressInMenus,
			addingCustomerDirectly,
			useMaterialsSpecialtyVisits,
			deletionExternal,

			// helpers
			id: null,
			dialog: false,

			title: null,

			loading: false,
			EntitiesLoading: false,
			scanDialog: false,
			scanError: null,
			scanHandled: false,
			locationChecking: false,
			locationValid: null,
			locationError: null,
			locationPermissionChecked: false,
			rules,
            
			sampleCounter: 1,
			competitorCounter: 0,
			orderItemCounter: 0,

			visitDateMenu: false,
			orderDeliveryDateMenu: false,
			dialogSectionTab: 0,
			moment,

			oldVisitPaymentAmount: null,
			originalVisitPayload: null,
			originalOrderPayload: null,
			initialOrderId: null,
			
			isFilledPlanedSamplesFilled: false,
			isSamplesPlanedChanged: false,
			
			// check availability
			isTyping: false,
			entitySearchInput: null,
			allEntities: [],

			// data
			dirType: null,
			priceTypeId: null,
			visitDate: null,
			regionId: null,
			entityId: null,
			personsNames: [],
			// samples: [{ id: 1, data: { materialId: null, amount: null } }],
			samples: [],
			orderItems: [],
			orderDeliveryDate: null,
			coreMessage: null,
			durationInMinutes: null,
			otherSamples: null,
			visitNotes: null,
			objection: null,
			nextCall: null,
			unavailableMaterialIds: [],
			competitors: [],
			centerNotes: null,
			paymentMethodId: null,
			paidAmount: null,
			enableVacations,

			isHoliday: null,
			permissionsNamesEnum,
			rolePermission
		}
	},

	watch: {
		dialog(val) {
			if (val && !this.customersTypes.length) {
				this.getHoliday(this.visitDate)
				this.$store.dispatch('entities/fetchEntityType');
			} if (val && !this.id) {
				this.getHoliday(this.visitDate)
				this.fetchEntities()
				this.ensureLocationPermissionOnDialogOpen()
				if (this.shouldCheckVisitLocation() && this.entityId) {
					this.checkVisitLocation()
				}
			} else if (!val) {
				this.locationChecking = false
				this.locationValid = null
				this.locationError = null
				this.locationPermissionChecked = false
			}
		},

		entityId(val) {
			if (!this.dialog || this.id) return
			if (!this.shouldCheckVisitLocation()) return

			if (!val) {
				this.locationChecking = false
				this.locationValid = null
				this.locationError = null
				return
			}

			this.checkVisitLocation()
		},

		scanDialog(val) {
			if (!val) {
				this.scanError = null
				this.scanHandled = false
			}
		},

		availableRepTabs() {
			if (this.dialogSectionTab >= this.availableRepTabs.length) {
				this.dialogSectionTab = 0
			}
		}
	},

	methods: {
		shouldCheckVisitLocation() {
			return Boolean(useVisitOrderRequest && useVisitLocationCheck)
		},

		ensureLocationPermissionOnDialogOpen() {
			if (!this.shouldCheckVisitLocation() || this.locationPermissionChecked) {
				return
			}

			this.locationPermissionChecked = true

			if (!navigator.geolocation) {
				this.locationValid = false
				this.locationError = 'GPS is not available on this device.'
				return
			}

			if (!navigator.permissions || !navigator.permissions.query) {
				this.requestLocationPermissionIfNeeded()
				return
			}

			navigator.permissions.query({ name: 'geolocation' })
				.then((status) => {
					if (status.state === 'granted') {
						return
					}

					if (status.state === 'denied') {
						this.locationValid = false
						this.locationError = 'Location permission is denied on this device.'
						return
					}

					this.requestLocationPermissionIfNeeded()
				})
				.catch(() => {
					this.requestLocationPermissionIfNeeded()
				})
		},

		requestLocationPermissionIfNeeded() {
			navigator.geolocation.getCurrentPosition(
				() => {
					this.locationError = null
				},
				(error) => {
					this.locationValid = false
					this.locationError = error?.message || 'Unable to access device location.'
				},
				{
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 0,
				}
			)
		},

		checkVisitLocation() {
			if (!this.shouldCheckVisitLocation()) {
				this.locationChecking = false
				this.locationValid = null
				this.locationError = null
				return Promise.resolve(null)
			}

			if (!this.dialog || this.id || !this.entityId) {
				this.locationChecking = false
				this.locationValid = null
				this.locationError = null
				return Promise.resolve(null)
			}

			if (!navigator.geolocation) {
				this.locationChecking = false
				this.locationValid = false
				this.locationError = 'GPS is not available on this device.'
				return Promise.resolve(null)
			}

			this.locationChecking = true
			this.locationValid = null
			this.locationError = null

			return new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 0,
				})
			})
				.then((position) => {
					return this.$store.dispatch('visits/checkLocation', {
						entityId: this.entityId,
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					})
				})
				.then((response) => {
					const isValid = Boolean(
						response?.isValid ??
						response?.valid ??
						response?.success ??
						response?.data?.isValid ??
						response?.data?.valid ??
						response?.data?.success ??
						response
					)

					this.locationValid = isValid
					this.locationError = isValid ? null : 'You are not at the customer location.'

					return response
				})
				.catch((error) => {
					this.locationValid = false
					this.locationError = error?.response?.data?.message || error?.message || 'Unable to verify your location.'
					return null
				})
				.finally(() => {
					this.locationChecking = false
				})
		},

		openScanDialog() {
			this.scanError = null
			this.scanHandled = false
			this.scanDialog = true
		},

		onQrInit(promise) {
			promise.catch(error => {
				this.scanError = error && error.message ? error.message : 'Unable to access camera'
			})
		},

		onQrDecode(rawValue) {
			if (this.scanHandled) return
			this.scanError = null
			this.scanHandled = true

			this.$store.dispatch('entities/fetchQr', { qrCodeData: rawValue })
				.then(serverEntity => {
					const d = { id: '51cc7ba9-0f8b-4d4d-3d67-08dc315c4a8c', name: 'انس المهايني pharma', typeId: 'efe65914-58c3-47a4-8185-22f8fedac069', regionId: '0fd0226e-8316-4770-c9da-08dc315b2a3b' }
					console.log('Decoded QR data:', rawValue, 'Server entity response:', serverEntity)
					// if (serverEntity) {
					// 	return this.applyServerEntityPayload(serverEntity)
					// }

					// Fallback: حاول قراءة الـ QR كـ string
					// const payload = this.parseQrPayload(rawValue)
					// if (!payload) {
					// 	this.scanHandled = false
					// 	this.scanError = 'Invalid QR payload'
					// 	return
					// }
					// return this.applyQrPayload(payload)
					if (serverEntity) {
						this.regionId = d.regionId
						this.dirType = d.typeId
						this.entityId = d.id
					}
				})
				.then(() => {
					this.scanDialog = false
					this.$eventBus.$emit('show-snackbar', 'QR data applied')
				})
				.catch(error => {
					this.scanHandled = false
					this.scanError = error && error.message ? error.message : 'Failed to apply QR data'
				})
		},

		parseQrPayload(rawValue) {
			if (!rawValue || typeof rawValue !== 'string') return null
			const value = rawValue.trim()
			if (!value) return null

			if (/^\d+$/.test(value)) {
				return { entityId: Number(value) }
			}

			try {
				return JSON.parse(value)
			} catch (error) {
				// Continue with non-JSON parsing.
			}

			try {
				const asUrl = new URL(value)
				const queryData = {}
				asUrl.searchParams.forEach((val, key) => {
					queryData[key] = val
				})
				return Object.keys(queryData).length ? queryData : null
			} catch (error) {
				// Continue with key/value parsing.
			}

			const items = value.split(/[;,]/).map(item => item.trim()).filter(Boolean)
			if (!items.length) return null

			const parsed = items.reduce((res, item) => {
				const separatorIndex = item.indexOf('=') !== -1 ? item.indexOf('=') : item.indexOf(':')
				if (separatorIndex === -1) return res
				const key = item.slice(0, separatorIndex).trim()
				const val = item.slice(separatorIndex + 1).trim()
				if (key) res[key] = val
				return res
			}, {})

			return Object.keys(parsed).length ? parsed : null
		},

		normalizePayloadKey(key) {
			return String(key || '').trim().toLowerCase().replace(/[\s_-]/g, '')
		},

		normalizeQrPayload(payload) {
			return Object.keys(payload || {}).reduce((res, key) => {
				res[this.normalizePayloadKey(key)] = payload[key]
				return res
			}, {})
		},

		pickPayloadValue(payload, keys) {
			for (let i = 0; i < keys.length; i++) {
				const value = payload[this.normalizePayloadKey(keys[i])]
				if (value !== undefined && value !== null && value !== '') {
					return value
				}
			}
			return null
		},

		reset() {
			if (this.$refs.form) this.$refs.form.resetValidation();

			this.id = null;
			this.priceTypeId = null;
			this.sampleCounter = 1;
			this.competitorCounter = null;
			this.dialogSectionTab = 0;

			this.regionId = null;
			this.entityId = null;
			// this.samples = [{ id: 1, data: { materialId: null, amount: null } }];
			this.personsNames = [];
			this.samples = [];
			this.orderItems = [];
			this.orderDeliveryDate = null;
			this.orderItemCounter = 0;
			this.coreMessage = null;
			this.durationInMinutes = null;
			this.otherSamples = null;
			this.visitNotes = null;
			this.objection = null;
			this.nextCall = null;
			this.unavailableMaterialIds = [];
			this.competitors = [];
			this.paidAmount = null;
			this.centerNotes = null;
			this.originalVisitPayload = null;
			this.originalOrderPayload = null;
			this.initialOrderId = null;
		},

		// samples
		addSample() {
			this.samples.push({
				id: ++this.sampleCounter,
				data: {
					materialId: null,
					amount: null,
					price: null,
					discount: null,
					isFree: false
				}
			});

			if (
				useMaterialsSpecialtyVisits &&
				this.isFilledPlanedSamplesFilled
			) {
				this.isSamplesPlanedChanged = true
			}
		},

		addOrderItem() {
			this.orderItems.push({
				id: ++this.orderItemCounter,
				data: {
					materialId: null,
					amount: null,
				}
			})
		},

		removeOrderItem(id) {
			const index = this.orderItems.findIndex(item => item.id === id)
			if (index === -1) return

			this.orderItems.splice(index, 1)
		},

		removeSample(id) {
			const index = this.samples.findIndex(c => c.id === id);
			this.samples.splice(index, 1);
			this.samples.forEach(sample => {
				if (sample.id > index) {
					sample.id--;
				}
			})
			this.sampleCounter--;

			if (
				useMaterialsSpecialtyVisits &&
				this.isFilledPlanedSamplesFilled
			) {
				this.isSamplesPlanedChanged = true
			}
		},

		updateSamplesPrices() {
			for (let i = 0; i < this.samples.length; i++) {
				this.samples[i].data.price =
					this.samples[i].data.isFree ? 0 : this.getMaterialPrice(this.samples[i].data.materialId);
			}
		},

		getMaterialPrice(materialId) {
			if (!materialId) return 0;
			const material = this.getMaterialById(materialId);
			const priceObj = (material.prices || []).find(c => c.priceId === this.priceTypeId);

			return (priceObj && priceObj.active ? priceObj.price || material.price : material.price) || 0;
		},

		checkMaterialDiscountLimit(materialId, materialDiscount) {			
			if (!materialId || !materialDiscount) return true;
			const material = this.getMaterialById(materialId);
			const priceObj = (material.prices || []).find(c => c.priceId === this.priceTypeId);
			const limitDiscount = priceObj && priceObj.active ? priceObj.priceDiscount : null;
			return !limitDiscount || (materialDiscount > 0 && materialDiscount <= limitDiscount); 
		},
        
		// competitor
		addCompetitor() {
			this.competitors.push({
				id: this.competitorCounter++,
				value: null
			})
		},

		removeCompetitor(index) {
			this.competitors.splice(index, 1);
			this.competitors.forEach(competitor => {
				if (competitor.id > index) {
					competitor.id--;
				}
			})
			this.competitorCounter--;
		},

		// find data
		getFullMaterialName(material) {
			const details = [
				this.getMaterialFormById(material.materialFormId).name,
				material.dose,
				material.strength
			];

			return `${material.name} (${details.filter(c => c != null).join(' ')})`;
		},

		fetchEntities() {
			this.EntitiesLoading = true;

			return this.$store.dispatch('entities/fetchAll', { entityType: this.dirType, regionId: this.regionId })
				.then(() => {
					this.allEntities = Array.from(this.entities);
				})
				.finally(() => {
					this.EntitiesLoading = false;
				})
		},

		buildOrderPayload(visitId = this.id, orderId = this.initialOrderId) {
			const orderItemsPayload = this.orderItems
				.map(({ data }) => ({
					materialId: data.materialId,
					amount: data.amount,
				}))
				.filter(item => item.materialId && item.amount)

			return {
				id: orderId || null,
				entityId: this.entityId,
				employeeId: this.userData?.id || null,
				visitId: visitId || null,
				orderCost: 0,
				isPosted: true,
				notes: '',
				orderDetail: orderItemsPayload.map(item => ({
					orderId: orderId || null,
					materialId: item.materialId,
					amount: item.amount,
					deliverDate: this.orderDeliveryDate || null,
					notes: '',
					personDelivered: '',
					isDone: false,
				}))
			}
		},

		buildGroupedSamplesPayload() {
			return this.samples.map(({ data }) => ({
				materialId: data.materialId,
				amount: data.amount,
				...(
					usePrices
						? {
							sellPrice: data.price,
							freeSample: data.isFree,
							priceDiscount: data.discount,
							paymentWay: data.paymentWay,
							priceTypeId: this.priceTypeId,
						}
						: {}
				)
			})).reduce((res, item) => {
				const index = res.findIndex(c => item.materialId === c.materialId)
				if (index === -1) res.push(item)
				else res[index].amount += item.amount
				return res
			}, [])
		},

		saveOrder(orderPayload, { forceCreate = false } = {}) {
			if (forceCreate || !this.initialOrderId) {
				return this.$store.dispatch('order/create', { order: orderPayload })
			}

			return this.$store.dispatch('order/update', {
				id: this.initialOrderId,
				order: orderPayload
			})
		},

		handleSaveSuccess(messageKey) {
			this.$eventBus.$emit('show-snackbar', this.$t(messageKey));
			this.$emit('on-save');
			this.dialog = false;
			if (usePrices) {
				this.$store.dispatch('profile/fetchBalance');
				this.$emit(
					'update-balance',
					this.paymentMethodId === paymentMethod.debit
						? this.paidAmount
						: this.total,
					this.oldVisitPaymentAmount
				);
				this.oldVisitPaymentAmount = null;
			}
		},

		updateItemsFromSpecialtyMaterials() {
			this.isFilledPlanedSamplesFilled = false;
			this.isSamplesPlanedChanged = false
			this.samples = [];
			let materials = [];
			const entity = this.allEntities.find(c => c.id === this.entityId);
			if (
				!useMaterialsSpecialtyVisits ||
				!this.specialtyMaterials.length ||
				!entity ||
				!entity.entityAddresses?.length
			) return;
			
			const specialtyMaterial =
				this.specialtyMaterials.find(c => c.specialtyId === entity.specialtyIds[0]);
			const address = entity.entityAddresses[0];
			
			if (!specialtyMaterial) return;
			const workload = specialtyMaterial.workloads.find(c => c.workloadId === address.workloadId);
			
			if (workload) {
				materials = workload.materials;
			} else {
				materials = specialtyMaterial.materials;
			};
			
			if (materials.length) {
				this.isFilledPlanedSamplesFilled = true;
				materials.forEach((c) => {
					this.samples.push({
						id: ++this.sampleCounter,
						data: {
							materialId: c.materialId,
							amount: c.amount,
							price: null,
							discount: null,
							isFree: false
						}
					})
				})
			}
		},

		// dialog functions
		addVisit(type, date) {
			this.reset();
			this.dirType = type;
			this.title = this.$t('Visits.add-visit');
			this.visitDate = moment().diff(date, 'days') <= 1 ? date : moment().format('YYYY-MM-DD');
			this.dialog = true;
		},

		editVisit(id) {
			this.reset();
			this.id = id;
			this.fillData();
			this.title = this.$t('Visits.edit-visit');
			this.dialog = true;
		},

		fillData() {
			const visit = this.getVisitById(this.id);
			this.dirType = visit.entity.entityTypeId;

			this.visitDate = moment(visit.createdAt).format('YYYY-MM-DD');
			this.regionId = visit.entityAddresses[0].regionId;
			this.entityId = visit.entity.id;
			this.coreMessage = visit.coreMessage;
			this.durationInMinutes = visit.durationInMinutes;
			this.otherSamples = visit.otherSamples;
			this.visitNotes = visit.notes;
			this.objection = visit.objections;
			this.nextCall = visit.purpose;
			this.centerNotes = visit.nextVisitNotes;
			this.personsNames =
				visit.residentDoctorNames?.split(',').map((c, i) => ({ id: i, name: c.trim() })) || [];
            
			visit.unavailableMaterials.forEach(materialName => {
				this.unavailableMaterialIds.push(this.getMaterialByName(materialName).id);
			})

			this.samples = [];
			visit.materialAmounts.forEach(material => {
				this.samples.push({
					id: ++this.sampleCounter,
					data: {
						materialId: material.materialId,
						amount: material.amount,
						price: usePrices ? (!material.freeSample ? material.sellPrice : 0) : null,
						discount: usePrices ? material.priceDiscount : null,
						// isFree: this.visitsConfig?.FreeSample ? material.freeSample : null,
						isFree: material.freeSample ? material.freeSample : null,
					}
				})
			});

			this.priceTypeId = visit.materialAmounts.length ? visit.materialAmounts[0]?.priceTypeId : null;
			this.paymentWay = visit.materialAmounts.length ? visit.materialAmounts[0]?.paymentWay : null;

			// Order data is fully independent from visit materials.
			// Keep order section empty until it is loaded from /order/visit/{visitId}.
			this.orderItems = []
			this.orderItemCounter = 0
			this.orderDeliveryDate = null

			if (visit.competitiveSamples.length > 0) {
				visit.competitiveSamples.forEach(sample => {
					this.competitors.push({
						id: this.competitorCounter++,
						value: null
					})
				});
			} else {
				this.competitors = []
			}
			
			if (usePrices) {
				this.paymentMethodId = visit.payment;
				this.paidAmount = visit.amountReceived;
				this.oldVisitPaymentAmount = this.total;
			}

			this.initialOrderId = visit.orderId || null
			if (this.permissionOrder.rolePermissions === this.rolePermission.undefined) {
				return
			}
			this.$store.dispatch('order/fetchOrderByVisitId', { visitId: this.id })
				.then((orderResponse) => {
					const fetchedOrder = orderResponse?.order || orderResponse || null
					let fetchedOrderDetails = []
					if (Array.isArray(orderResponse?.orderDetail)) {
						fetchedOrderDetails = orderResponse.orderDetail
					} else if (Array.isArray(fetchedOrder?.orderDetail)) {
						fetchedOrderDetails = fetchedOrder.orderDetail
					} else if (Array.isArray(fetchedOrder?.orderDetails)) {
						fetchedOrderDetails = fetchedOrder.orderDetails
					}

					if (fetchedOrder?.id || fetchedOrderDetails.length) {
						this.initialOrderId = fetchedOrder?.id || this.initialOrderId || null

						this.orderItems = fetchedOrderDetails.map((item, index) => ({
							id: index + 1,
							data: {
								materialId: item.materialId ?? item.data?.materialId ?? null,
								amount: item.amount ?? item.data?.amount ?? null,
							}
						}))
						this.orderItemCounter = this.orderItems.length

						const apiOrderDate =
							fetchedOrder?.orderDeliveryDate ||
							fetchedOrder?.deliverDate ||
							fetchedOrderDetails[0]?.deliverDate ||
							null
						this.orderDeliveryDate = apiOrderDate ? moment(apiOrderDate).format('YYYY-MM-DD') : null
					}
				})
				.catch(() => {})
				.finally(() => {
					const snapshotSamples = this.buildGroupedSamplesPayload()

					this.originalVisitPayload = JSON.stringify({
						entityId: this.entityId,
						coreMessage: this.coreMessage,
						durationInMinutes: this.durationInMinutes,
						otherSamples: this.otherSamples,
						notes: this.visitNotes,
						objections: this.objection,
						nextVisitNotes: this.centerNotes,
						purpose: this.nextCall,
						materialAmounts: snapshotSamples,
						unavailableMaterialIds: this.unavailableMaterialIds,
						competitiveSamples: this.competitors.map(c => c.value),
						residentDoctorNames: this.personsNames.map(c => c.name).join(', '),
						payment: this.paymentMethodId,
						amountReceived: this.paymentMethodId === paymentMethod.debit ? this.paidAmount : this.total,
					})

					this.originalOrderPayload = JSON.stringify(this.buildOrderPayload(this.id, this.initialOrderId))
				})
			
			this.fetchEntities();
		},

		submit() {
			if (this.$refs.form.validate()) {
				this.loading = true;

				// grouping duplicated records
				const samples = this.buildGroupedSamplesPayload()

				const visitPayload = {
					entityId: this.entityId,
					coreMessage: this.coreMessage,
					durationInMinutes: this.durationInMinutes,
					otherSamples: this.otherSamples,
					notes: this.visitNotes,
					objections: this.objection,
					nextVisitNotes: this.centerNotes,
					purpose: this.nextCall,
					materialAmounts: samples,
					unavailableMaterialIds: this.unavailableMaterialIds,
					competitiveSamples: this.competitors.map(c => c.value),
					residentDoctorNames: this.personsNames.map(c => c.name).join(', '),
					payment: this.paymentMethodId,
					amountReceived: this.paymentMethodId === paymentMethod.debit ? this.paidAmount : this.total,
					priceTypeId: this.priceTypeId,
				}

				const isCreateMode = !this.id

				if (isCreateMode) {
					this.$store.dispatch('visits/create', {
						...visitPayload,
						createdAt: this.visitDate,
					})
						.then((visitResponse) => {
							const createdVisitId = visitResponse?.data
							const orderPayload = this.buildOrderPayload(createdVisitId, null)
							if (!useVisitOrderRequest || !orderPayload.orderDetail.length) return null

							return this.saveOrder(orderPayload, { forceCreate: true })
						})
						.then(() => {
							this.handleSaveSuccess('Visits.visit-is-created')
						})
						.finally(() => {
							this.loading = false;
						})
				} else {
					const currentVisitPayload = JSON.stringify(visitPayload)
					const visitChanged = currentVisitPayload !== this.originalVisitPayload

					const currentOrderPayload = JSON.stringify(this.buildOrderPayload(this.id, this.initialOrderId))

					const orderChanged = currentOrderPayload !== this.originalOrderPayload

					let chain = Promise.resolve()

					if (visitChanged) {
						chain = chain.then(() => {
							return this.$store.dispatch('visits/update', {
								id: this.id,
								...visitPayload,
							})
						})
					}

					if (useVisitOrderRequest && orderChanged) {
						chain = chain.then(() => {
							const orderPayload = this.buildOrderPayload(this.id, this.initialOrderId)
							return this.saveOrder(orderPayload)
						})
					}

					chain
						.then(() => {
							this.handleSaveSuccess('Visits.visit-is-updated')
						})
						.finally(() => {
							this.loading = false;
						})
				}
			}
		},

		getHoliday (date) {
			const vacationDayDetails = this.vacationsDayDetails
			this.isHoliday = getDateColor(date, this.vacations, { vacationDayDetails })
		},

		normalizeVisitFieldNames (fieldNames) {
			const aliases = {
				Objection: ['Objections'],
				Objections: ['Objection'],
				UnavailableMaterial: ['UnavailableMaterials'],
				UnavailableMaterials: ['UnavailableMaterial'],
			}

			const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames]
			return [...new Set(names.flatMap(name => [name, ...(aliases[name] || [])]))]
		},

		normalizeRepresentativeType (value, fallback = 'Both') {
			const normalizedValue = String(value || '').trim().toLowerCase()
			const sections = {
				both: 'Both',
				scientific: 'Scientific',
				financial: 'Financial',
				marketing: 'Financial',
				shared: 'Both',
				none: 'None',
			}

			return sections[normalizedValue] || fallback
		},

		getCurrentDialogSection () {
			if (!this.availableRepTabs.length) {
				return 'None'
			}

			return this.availableRepTabs[this.dialogSectionTab] || this.availableRepTabs[0]
		},

		shouldRenderDialogSection (section) {
			if (!this.availableRepTabs.length) {
				return false
			}

			const normalizedSection = this.normalizeRepresentativeType(section, 'Both')
			const currentSection = this.getCurrentDialogSection()

			if (this.availableRepTabs.length > 1) {
				return currentSection === normalizedSection
			}

			return this.availableRepTabs.includes(normalizedSection)
		},

		getVisitFieldConfig (fieldNames) {
			const names = this.normalizeVisitFieldNames(fieldNames)

			const customerTypeField = this.visitConfigFieldsByCustomerType
				.find(field => names.includes(field.name))
			if (customerTypeField) {
				return customerTypeField
			}

			return this.fieldsVisits.find(field => names.includes(field.name)) || null
		},

		isVisitFieldEnabled (fieldNames, fallback = true) {
			const field = this.getVisitFieldConfig(fieldNames)

			if (typeof field?.booleanValue === 'boolean') {
				return field.booleanValue
			}

			const names = this.normalizeVisitFieldNames(fieldNames)

			for (let i = 0; i < names.length; i++) {
				const value = this.visitConfigByCustomerType?.[names[i]]
				if (typeof value === 'boolean') {
					return value
				}
			}

			for (let i = 0; i < names.length; i++) {
				const value = this.visitsConfig?.[names[i]]
				if (typeof value === 'boolean') {
					return value
				}
			}

			return fallback
		},

		showVisitFieldForSection (fieldNames, fallback = true, defaultSection = 'Both') {
			if (!this.isVisitFieldEnabled(fieldNames, fallback)) {
				return false
			}

			const field = this.getVisitFieldConfig(fieldNames)
			const fallbackSection = this.normalizeRepresentativeType(defaultSection, 'Both')
			const fieldSection = this.normalizeRepresentativeType(field?.representativeType, fallbackSection)
			const repSection = this.availableRepTabs.length > 1
				? 'Both'
				: (this.availableRepTabs[0] || 'None')
			const activeSection = this.getCurrentDialogSection()

			if (repSection === 'None') {
				return false
			}

			// اذا كان المندوب محدد القسم، لا يرى حقول القسم الآخر
			if (repSection !== 'Both' && fieldSection !== 'Both' && fieldSection !== repSection) {
				return false
			}

			// representativeType rules from API:
			// Both       -> يظهر في التابين
			// Scientific -> يظهر في التاب العلمي فقط
			// Financial  -> يظهر في التاب المالي فقط
			if (fieldSection === 'Both') {
				return true
			}

			return activeSection === fieldSection
		},

		configIsEmpty (obj) {
			return obj ? Object.keys(obj).length === 0 : true
		}
	},

	computed: {
		...mapState({
			balance: state => state.profile.balance,
			regions: state => state.regions.regions,
			priceTypes: state => state.priceTypes.priceTypes,
			allMaterials: state => state.materials.materials,
			transactions: state => state.materialsTransactions.transactions,
			materialForms: state => state.materialForms.materialForms,
			entities: state => state.entities.entities,
			specialtyMaterials: (state) => state.materials.specialtyMaterials,
			customersTypes: state => deletionExternal ? state.entities.customersTypes.filter(c => c.name !== 'Externals') : state.entities.customersTypes,
			vacationsDayDetails: state => state.vacations.vacationsDayDetails,
			vacations: state => state.vacations.vacations,
			// configurations
			visitsConfig: state => state.configurations.configurations?.visitsConfig,
			// visit by customer
			visitsConfigByCustomerType: state => state.configurations.visitsConfigByCustomerType,
			fieldsVisits: state => state.configurations.fieldsVisits,
			userData: state => state.auth.userData,
			salesSystem: state => state.configurations.systemConfigValue.properties?.find(c => c.key === 'salesSystem')?.value,
			permissionOrder: state => state.auth.userData.role.permissions.find(c => c.permissionsNames === 36)
			// debitPermission: state => state.auth.userData.role.permissions.find(c => c.permissionsNames === permissionsNamesEnum.DebtSale)	
		}),

		...mapGetters({
			getVisitById: 'visits/getVisitById',
			getMaterialById: 'materials/getMaterialById',
			getMaterialByName: 'materials/getMaterialByName',
			getMaterialFormById: 'materialForms/getMaterialFormById',
			repType: 'auth/repType',
		}),

		materials() {
			const availableMaterials = [];
			const availableMaterialsIds = this.transactions.map(c => c.materialId);
			availableMaterialsIds.forEach(id => availableMaterials.push(this.getMaterialById(id)));
			return availableMaterials;
		},

		total() {
			return this.samples.map(c => c.data).reduce((res, c) => {
				const priceNet = c.amount * c.price;
				return res + (priceNet - (c.discount / 100) * priceNet);
			}, 0);
		},

		hasDeliveryDateWithoutItems() {
			return Boolean(this.orderDeliveryDate && this.orderItems.length === 0)
		},

		paymentMethods() {
			return [
				{ id: paymentMethod.cash, name: this.$T('payment-method.cash') },
				{ id: paymentMethod.credit, name: this.$T('payment-method.credit') },
				{ id: paymentMethod.debit, name: this.$T('payment-method.debit') },
			]
		},

		availableRepTabs () {
			if (this.repType === 'Both') {
				return ['Scientific', 'Financial']
			}

			if (this.repType === 'Scientific') {
				return ['Scientific']
			}

			if (this.repType === 'Financial') {
				return ['Financial']
			}

			return []
		},

		visitConfigByCustomerTypeItem () {
			return (this.customersTypes.length && this.customersTypes.length > 0)
				? this.visitsConfigByCustomerType.find(c => c.key === this.dirType)
				: null
		},

		visitConfigByCustomerType () {
			return this.visitConfigByCustomerTypeItem?.data || {}
		},

		visitConfigFieldsByCustomerType () {
			return this.visitConfigByCustomerTypeItem?.fields || []
		},
	},

	mounted() {
		if (!this.customersTypes.length) {
			this.$store.dispatch('entities/fetchEntityType');
		}
	}
}
</script>

<style scoped>
.order-expander {
	border-radius: 12px;
}

.order-expander__panel {
	border: 1px solid #e0e0e0;
	border-radius: 12px;
	background: linear-gradient(180deg, #ffffff 0%, #f9fcfc 100%);
}

.order-expander__content {
	border-top: 1px dashed #d9e6e6;
}

.order-expander__items-card {
	border-color: #d7e6e6 !important;
	box-shadow: 0 2px 10px rgba(0, 77, 77, 0.04);
}

.order-expander__empty {
	background: #fafcfc;
	border-color: #dfe9e9 !important;
}

.order-expander__date-input :deep(.v-input__slot) {
	background-color: #fcfefe;
}

</style>